import ScButton from "@/src/components/ui-kit/button";
import { User, UserRole } from "@/src/models/user.model";
import {
  useInviteUsers,
  type InviteProgress,
  type InviteUsersResult,
} from "@/src/services/users.blocks.api";
import { useTheme } from "@/src/styles/theme-provider";
import {
  AddCircle,
  ArrowDown2,
  CloseCircle,
  DocumentDownload,
  ImportCurve,
} from "iconsax-react-nativejs";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Modal, Pressable, Text, TextInput, View } from "react-native";
import ImportUsersModal from "./import-user-modal";
import {
  downloadFailedRows,
  type DroppedUserRow,
  type ImportedUserRow,
  type ImportExcelResult,
} from "@/src/utils/import-excel-users";
import { capitalizeName, formatUserName } from "@/src/utils/string.utils";

type InviteUserModalProps = Readonly<{
  visible: boolean;
  onClose: () => void;
}>;

/**
 * First name, last name and email are all required to add someone to the
 * invite list — the account is created active (see inviteUser in
 * users.blocks.api.ts) with no further onboarding step to fill a missing
 * name in later, so it has to be captured here.
 *
 * `errorKey` drives the react-hook-form `required` rule. `required` is also
 * read for the placeholder, so an empty field says so rather than leaving the
 * user hunting for what is blocking Add.
 */
const FIELD_CONFIG = {
  firstName: {
    placeholder: "LABEL_FIRST_NAME",
    keyboardType: "default",
    autoCapitalize: "sentences",
    required: true,
    errorKey: "ERROR_FIRST_NAME_REQUIRED",
  },
  lastName: {
    placeholder: "LABEL_LAST_NAME",
    keyboardType: "default",
    autoCapitalize: "sentences",
    required: true,
    errorKey: "ERROR_LAST_NAME_REQUIRED",
  },
  email: {
    placeholder: "LABEL_EMAIL_ADDRESS",
    keyboardType: "email-address",
    autoCapitalize: "none",
    required: true,
    errorKey: "ERROR_EMAIL_REQUIRED",
  },
} as const;

const formatRole = (role: string) =>
  role
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const InviteUserModal = ({ visible, onClose }: InviteUserModalProps) => {
  const { t } = useTranslation();
  const { themeVars, varStyle } = useTheme();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<User>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
    },
  });

  const formValues = watch();
  const [inviteList, setInviteList] = useState<User[]>([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const [progress, setProgress] = useState<InviteProgress | null>(null);
  const [doneResult, setDoneResult] = useState<InviteUsersResult | null>(null);
  // Rows the Excel parser dropped before anything was sent (bad email, missing
  // name, in-sheet duplicate) — kept so the Done screen's "Download failed
  // rows" can combine them with the send-time failures into one file. Empty
  // for the manual-add path, which has no such thing.
  const [importDropped, setImportDropped] = useState<DroppedUserRow[]>([]);
  const [importSourceFileName, setImportSourceFileName] = useState("");
  const { inviteUsers } = useInviteUsers();
  const isRunning = progress !== null && doneResult === null;

  /**
   * Capitalized here so a typed name is stored the same way an imported one is.
   *
   * `autoCapitalize: "sentences"` on the name fields is only a keyboard hint — it
   * pre-shifts a mobile on-screen keyboard and transforms nothing, so a pasted or
   * lower-cased entry stores as typed. On web, where this app actually ships, the
   * attribute is ignored by desktop browsers outright. The invite list feeds the
   * IAM account create and the profile insert, so this is the last point where the
   * value can be normalized before it becomes the stored name.
   */
  const handleAdd = (data: User) => {
    setInviteList((previousList) => [
      ...previousList,
      {
        ...data,
        firstName: capitalizeName(data.firstName),
        lastName: capitalizeName(data.lastName),
        role: UserRole.MEMBER,
      },
    ]);
    reset();
  };

  const handleUpdateRole = (index: number, role: UserRole) => {
    setInviteList((previousList) =>
      previousList.map((invitedUser, userIndex) =>
        userIndex === index ? { ...invitedUser, role } : invitedUser,
      ),
    );
    setOpenMenuIndex(null);
  };

  const handleRemoveUser = (index: number) => {
    setInviteList((previousList) =>
      previousList.filter((_invitedUser, userIndex) => userIndex !== index),
    );
  };

  const runInvite = (users: (User | ImportedUserRow)[]) => {
    if (users.length === 0) return;
    setDoneResult(null);
    setProgress({ completed: 0, total: users.length });
    inviteUsers.mutate(
      { users, onProgress: setProgress },
      {
        onSuccess: (result) => setDoneResult(result),
        // The mutation-level onError (in useInviteUsers) already toasts the
        // reason — this just gets the modal out of the stuck "running" state.
        onError: () => setProgress(null),
      },
    );
  };

  const handleInviteUsers = () => {
    setImportDropped([]);
    setImportSourceFileName("");
    runInvite(inviteList);
    setInviteList([]);
  };

  const handleImportedUsers = (result: ImportExcelResult) => {
    setImportDropped(result.dropped);
    setImportSourceFileName(result.sourceFileName);
    runInvite(result.imported);
    setShowImportModal(false);
  };

  const handleDownloadFailed = () => {
    const sendFailures: DroppedUserRow[] = (doneResult?.failures ?? []).map(
      (failure) => ({ raw: { Email: failure.email }, reason: failure.reason }),
    );
    downloadFailedRows(importSourceFileName, [
      ...importDropped,
      ...sendFailures,
    ]).catch((error) => console.error("Download of failed rows failed", error));
  };

  const handleDone = () => {
    setProgress(null);
    setDoneResult(null);
    setImportDropped([]);
    setImportSourceFileName("");
    onClose();
  };

  const isAddDisabled =
    !formValues.email?.trim() ||
    !formValues.firstName?.trim() ||
    !formValues.lastName?.trim();

  return (
    <Modal transparent visible={visible} animationType="fade">
      <Pressable
        style={varStyle}
        className="justify-center items-center bg-shadow-dark flex-1 px-6"
        onPress={onClose}
      >
        <Pressable
          className="rounded-2xl p-6 gap-4 bg-ash w-full max-w-2xl"
          onPress={(e) => e.stopPropagation()}
        >
          <View className="flex-row justify-between items-center">
            <Text className="text-xl font-semibold text-onyx">
              {t("LABEL_INVITE_USER")}
            </Text>
            <Pressable
              onPress={onClose}
              className="rounded-full hover:bg-ash-3"
            >
              <CloseCircle size={24} color={themeVars["--color-ash-2"]} />
            </Pressable>
          </View>

          {isRunning && progress && (
            <View className="gap-2">
              <Text className="text-sm font-medium text-onyx">
                {t("LABEL_INVITING_PROGRESS", {
                  completed: progress.completed,
                  total: progress.total,
                })}
              </Text>
              <View className="h-2 rounded-full bg-ash-3 overflow-hidden">
                <View
                  className="h-2 rounded-full bg-primary"
                  style={{
                    width: `${Math.min(
                      100,
                      (progress.completed / Math.max(progress.total, 1)) * 100,
                    )}%`,
                  }}
                />
              </View>
              {progress.current && (
                <Text className="text-xs text-gray-dark" numberOfLines={1}>
                  {t("LABEL_INVITING_USER", {
                    name: formatUserName(
                      progress.current.firstName,
                      progress.current.lastName,
                    ),
                    email: progress.current.email,
                  })}
                </Text>
              )}
            </View>
          )}

          {doneResult && (
            <View className="gap-2">
              <Text className="text-sm font-medium text-onyx">
                {t("LABEL_INVITE_DONE", {
                  created: doneResult.createdCount,
                  failed: doneResult.failures.length,
                })}
              </Text>
              {importDropped.length > 0 && (
                <Text className="text-xs text-amber-600">
                  {t("LABEL_ROWS_SKIPPED_BEFORE_SEND", {
                    count: importDropped.length,
                  })}
                </Text>
              )}
              {doneResult.failures.length > 0 && (
                <View className="max-h-32 gap-1">
                  {doneResult.failures.map((failure) => (
                    <Text
                      key={failure.email}
                      className="text-xs text-crimson"
                      numberOfLines={1}
                    >
                      {failure.email}: {failure.reason}
                    </Text>
                  ))}
                </View>
              )}
              {importDropped.length + doneResult.failures.length > 0 && (
                <Pressable
                  onPress={handleDownloadFailed}
                  className="flex-row items-center gap-1.5 self-start"
                >
                  <DocumentDownload size={16} className="text-primary" />
                  <Text className="text-xs text-primary font-medium">
                    {t("BTN_DOWNLOAD_FAILED_ROWS", {
                      count: importDropped.length + doneResult.failures.length,
                    })}
                  </Text>
                </Pressable>
              )}
              <ScButton
                text="BTN_CLOSE"
                mode="outlined"
                onPress={handleDone}
                className="self-end"
              />
            </View>
          )}

          {!isRunning && !doneResult && (
            <>
          <View className="flex-col md:flex-row gap-2 md:flex-wrap">
            {(["firstName", "lastName", "email"] as const).map((field) => {
              const config = FIELD_CONFIG[field];
              return (
                <Controller
                  key={field}
                  control={control}
                  name={field}
                  rules={{
                    required: config.errorKey ? t(config.errorKey) : false,
                    validate:
                      field === "email"
                        ? (fieldValue) => {
                            if (!fieldValue) return true;
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            return (
                              emailRegex.test(fieldValue) ||
                              t("ERROR_INVALID_EMAIL")
                            );
                          }
                        : undefined,
                  }}
                  render={({ field: { onChange, value } }) => (
                    <TextInput
                      placeholder={
                        config.required
                          ? t(config.placeholder)
                          : `${t(config.placeholder)} (${t("LABEL_OPTIONAL")})`
                      }
                      placeholderTextColor={themeVars["--color-slate-default"]}
                      value={value}
                      onChangeText={onChange}
                      keyboardType={config.keyboardType}
                      autoCapitalize={config.autoCapitalize}
                      className="w-full md:flex-1 bg-chalk md:bg-transparent rounded-lg py-3 px-2 md:border md:border-ash-3 text-onyx"
                    />
                  )}
                />
              );
            })}
            <ScButton
              text="BTN_ADD"
              mode="outlined"
              disabled={isAddDisabled}
              disabledClassName="text-gray-dark border-ash-3"
              icon={<AddCircle color={themeVars["--color-primary-default"]} />}
              onPress={handleSubmit(handleAdd)}
            />
          </View>

          <View className="mb-2 gap-0.5">
            {(["firstName", "lastName", "email"] as const).map(
              (field) =>
                errors[field] && (
                  <Text key={field} className="text-crimson text-xs">
                    {errors[field]?.message}
                  </Text>
                ),
            )}
          </View>

          {inviteList.length > 0 && (
            <>
              <Text className="text-sm font-medium text-onyx">
                {t("LABEL_ADDED_MEMBERS")} ({inviteList.length})
              </Text>

              {inviteList.map((invitedUser, index) => {
                const isMenuOpen = openMenuIndex === index;
                const rowZIndex = isMenuOpen ? "z-[100]" : "z-[1]";
                const isMemberActive = invitedUser?.role === UserRole.MEMBER;
                const isAdminActive = invitedUser?.role === UserRole.ADMIN;

                return (
                  <View
                    key={invitedUser.email}
                    className={`flex-row items-center justify-between md:border-t md:border-ash-3 px-3 py-2 relative ${rowZIndex}`}
                  >
                    <Text className="flex-1 text-onyx">
                      {invitedUser?.email}
                    </Text>

                    <View className="relative mr-2">
                      <Pressable
                        onPress={() =>
                          setOpenMenuIndex(isMenuOpen ? null : index)
                        }
                        className="flex-row items-center justify-between gap-2 md:border md:border-primary rounded-full px-4 py-1.5 bg-chalk hover:bg-ash-3 hover:rounded-full"
                      >
                        <Text className="text-sm text-primary">
                          {invitedUser?.role
                            ? formatRole(invitedUser.role)
                            : formatRole(UserRole.MEMBER)}
                        </Text>
                        <ArrowDown2
                          size={18}
                          variant="Linear"
                          color={themeVars["--color-primary-default"]}
                        />
                      </Pressable>

                      {isMenuOpen && (
                        <View className="absolute top-10 right-0 bg-chalk md:border md:border-ash-3 rounded-xl overflow-hidden min-w-[120px] shadow-lg">
                          <Pressable
                            onPress={() =>
                              handleUpdateRole(index, UserRole.MEMBER)
                            }
                            className={`px-4 py-3 ${isMemberActive ? "bg-primary-2 rounded-[8px] m-[2px] hover:bg-primary-2" : "hover:bg-ash-3"}`}
                          >
                            <Text
                              className={`text-sm ${isMemberActive ? "text-primary" : "text-onyx"}`}
                            >
                              {t("LABEL_MEMBER")}
                            </Text>
                          </Pressable>

                          <Pressable
                            onPress={() =>
                              handleUpdateRole(index, UserRole.ADMIN)
                            }
                            className={`px-4 py-3 ${isAdminActive ? "bg-primary-2 rounded-[8px] m-[2px] hover:bg-primary-2" : "hover:bg-ash-3"}`}
                          >
                            <Text
                              className={`text-sm ${isAdminActive ? "text-primary" : "text-onyx"}`}
                            >
                              {t("LABEL_ADMIN")}
                            </Text>
                          </Pressable>
                        </View>
                      )}
                    </View>

                    <Pressable
                      onPress={() => handleRemoveUser(index)}
                      className="rounded-full hover:bg-ash-3"
                    >
                      <CloseCircle
                        size={20}
                        color={themeVars["--color-ash-2"]}
                      />
                    </Pressable>
                  </View>
                );
              })}
            </>
          )}

          <View className="flex-row justify-between items-center mt-4">
            <ScButton
              onPress={() => setShowImportModal(true)}
              text="BTN_IMPORT"
              mode="outlined"
              className="rounded"
              icon={
                <ImportCurve color={themeVars["--color-primary-default"]} />
              }
            />
            <ScButton
              text="BTN_INVITE"
              disabled={inviteList.length === 0}
              icon={<AddCircle color="white" />}
              onPress={handleInviteUsers}
              disabledClassName="text-gray-dark border-ash-3"
            />
          </View>
            </>
          )}
        </Pressable>
      </Pressable>

      <ImportUsersModal
        visible={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImportedUsers}
      />
    </Modal>
  );
};

export default InviteUserModal;
